import { IconForward } from '@shared/ui/icons';
import { formatMoney, formatMillions } from '../financierFormat';
import {
  PipelineWrapper,
  StageCard,
  StageHeader,
  StageBadge,
  StageTitle,
  StageMetrics,
  MetricCount,
  MetricAmount,
  CarryoverBadge,
  FactSubGrid,
  FactRow,
} from './styled';
import type { PipelineMetrics } from './types';

interface RequestsPipelineProps {
  pipelineStage: string;
  onStageChange: (stage: string) => void;
  /** `null` — список заявок не загрузился: вместо нулей прочерки. */
  metrics: PipelineMetrics | null;
}

const NO_METRICS: PipelineMetrics = {
  formingCount: 0,
  formingAmount: 0,
  gatesPassedCount: 0,
  gatesPassedAmount: 0,
  approvedCount: 0,
  approvedAmount: 0,
  cutCarryoverTotal: 0,
  inBankCount: 0,
  inBankAmount: 0,
  paidTotal: 0,
  partiallyPaidTotal: 0,
  unpaidTotal: 0,
};

export function RequestsPipeline({
  pipelineStage,
  onStageChange,
  metrics: loaded,
}: RequestsPipelineProps) {
  const metrics = loaded ?? NO_METRICS;
  const money = (v: number) => (loaded ? formatMoney(v) : '—');
  const count = (v: number, unit: string) => (loaded ? `${v} ${unit}` : '—');
  return (
    <PipelineWrapper>
      <StageCard
        $active={pipelineStage === 'forming'}
        onClick={() => onStageChange(pipelineStage === 'forming' ? 'all' : 'forming')}
        title="Черновики, заявки на финконтроле и готовые для Гендиректора с предупреждением хотя бы одного гейта"
      >
        <StageHeader>
          <StageBadge>1</StageBadge>
          <StageTitle>На проверке</StageTitle>
        </StageHeader>
        <StageMetrics>
          <MetricCount>{count(metrics.formingCount, 'заявок')}</MetricCount>
          <MetricAmount>{money(metrics.formingAmount)}</MetricAmount>
        </StageMetrics>
      </StageCard>

      <StageCard
        $active={pipelineStage === 'gates_passed'}
        $tone="#10b981"
        onClick={() => onStageChange(pipelineStage === 'gates_passed' ? 'all' : 'gates_passed')}
        title="Готовы к передаче Гендиректору без предупреждений гейтов и уже переданные ему на решение"
      >
        <StageHeader>
          <StageBadge $tone="#10b981">2</StageBadge>
          <StageTitle>Для Гендиректора</StageTitle>
        </StageHeader>
        <StageMetrics>
          <MetricCount>{count(metrics.gatesPassedCount, 'заявок')}</MetricCount>
          <MetricAmount>{money(metrics.gatesPassedAmount)}</MetricAmount>
        </StageMetrics>
      </StageCard>

      <StageCard
        $active={pipelineStage === 'approved'}
        $tone="#0ea5e9"
        onClick={() => onStageChange(pipelineStage === 'approved' ? 'all' : 'approved')}
      >
        <StageHeader>
          <StageBadge $tone="#0ea5e9">3</StageBadge>
          <StageTitle>Утверждено</StageTitle>
        </StageHeader>
        <StageMetrics>
          <MetricCount>{count(metrics.approvedCount, 'заявок')}</MetricCount>
          <MetricAmount>{money(metrics.approvedAmount)}</MetricAmount>
          {metrics.cutCarryoverTotal > 0 && (
            <CarryoverBadge>
              <IconForward size={12} />
              Перенос в след. реестр: {formatMillions(metrics.cutCarryoverTotal)}
            </CarryoverBadge>
          )}
        </StageMetrics>
      </StageCard>

      <StageCard
        $active={pipelineStage === 'in_bank'}
        $tone="#8b5cf6"
        onClick={() => onStageChange(pipelineStage === 'in_bank' ? 'all' : 'in_bank')}
      >
        <StageHeader>
          <StageBadge $tone="#8b5cf6">4</StageBadge>
          <StageTitle>В 1С / В банке</StageTitle>
        </StageHeader>
        <StageMetrics>
          <MetricCount>{count(metrics.inBankCount, 'п/п')}</MetricCount>
          <MetricAmount>{money(metrics.inBankAmount)}</MetricAmount>
        </StageMetrics>
      </StageCard>

      <StageCard
        $active={pipelineStage === 'executed'}
        $tone="#10b981"
        onClick={() => onStageChange(pipelineStage === 'executed' ? 'all' : 'executed')}
      >
        <StageHeader>
          <StageBadge $tone="#10b981">5</StageBadge>
          <StageTitle>Факт по выписке</StageTitle>
        </StageHeader>
        <FactSubGrid>
          <FactRow $color="#10b981">
            <span>Оплачено всего:</span>
            <span>{money(metrics.paidTotal)}</span>
          </FactRow>
          <FactRow $color="#f59e0b">
            <span>в т.ч. частично:</span>
            <span>{money(metrics.partiallyPaidTotal)}</span>
          </FactRow>
          <FactRow>
            <span>Не оплачено:</span>
            <span>{money(metrics.unpaidTotal)}</span>
          </FactRow>
        </FactSubGrid>
      </StageCard>
    </PipelineWrapper>
  );
};
